import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { mintDocumentViewUrl } from '../services/documentViewUrl';
import { ragService } from '../services/ragService';
import type { ChunkPositionData } from '../services/ragService';
import type { ChunkHighlight } from '../libs/document-viewer/types';
import './PDFDocumentViewerTab.css';

// Lazy load DocumentViewer for better performance (only loads when needed)
const DocumentViewer = lazy(() => import('../libs/document-viewer'));

interface ChunkData {
  chunk_id: string;
  text: string;
  chunk_score: number;
  page_number?: number;
  char_start?: number;
  char_end?: number;
}

interface PDFDocumentViewerTabProps {
  documentId: string;
  documentTitle: string;
  kbId: string;
  chunks?: ChunkData[];
  onClose?: () => void;
}

interface OCRPageData {
  pageNumber: number;
  ocrText: string;
  textFormat: 'markdown' | 'plain';
}

interface OCRPagesResponse {
  documentId: string;
  processedDate: string;
  pages: OCRPageData[];
  pageBoundaries: number[];
  totalPages: number;
}

interface ChunkDetailsResponse {
  chunks: Array<{
    chunkId: string;
    documentId: string;
    charStart: number;
    charEnd: number;
    pageNumber: number;
    chunkIndex: number;
    text: string;
  }>;
}

const PDFDocumentViewerTab: React.FC<PDFDocumentViewerTabProps> = ({
  documentId,
  documentTitle,
  kbId,
  chunks = [],
  onClose,
}) => {
  const { i18n, t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ocrData, setOcrData] = useState<OCRPagesResponse | null>(null);
  const [enrichedChunks, setEnrichedChunks] = useState<ChunkHighlight[]>([]);
  const [isConverting, setIsConverting] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [forceBackendBboxes, setForceBackendBboxes] = useState(false);

  // PDF URL carries a short-lived, single-document view token (never the session JWT).
  // Minted on mount and again on every reload; state so we can swap in a converted blob URL.
  const [pdfUrl, setPdfUrl] = useState('');
  useEffect(() => {
    let cancelled = false;
    mintDocumentViewUrl(kbId, documentId, 'pdf', reloadCounter ? `recalc=${reloadCounter}` : '').then(url => {
      if (!cancelled) setPdfUrl(url);
    }).catch(err => setError(err instanceof Error ? err.message : 'Failed to authorize document view'));
    return () => { cancelled = true; };
  }, [kbId, documentId, reloadCounter]);

  // Fetch OCR pages and chunk details
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch OCR pages for the document
      const ocrResponse = await apiClient.get<OCRPagesResponse>(
        `/knowledge-bases/${kbId}/documents/${documentId}/ocr-pages`
      );
      setOcrData(ocrResponse.data);

      // Fetch cached positions from backend
      // Note: Only PDF.js-computed positions (native text layer) are cached.
      // Tesseract-computed positions (scanned PDFs) are never cached and will always be recomputed.
      const cachedPositions: Map<string, ChunkPositionData> = new Map();
      
      try {
        const positionsResponse = await ragService.getChunkPositions(kbId, documentId);
        for (const pos of positionsResponse.positions) {
          if (pos.position) {
            cachedPositions.set(pos.chunk_id, pos.position);
          }
        }
      } catch {
        console.warn('[PDFDocumentViewerTab] No cached positions available - will compute fresh');
      }

      // If we have chunks, fetch their positioning details
      if (chunks.length > 0) {
        const chunkIds = chunks.map(c => c.chunk_id);
        const detailsResponse = await apiClient.post<ChunkDetailsResponse>(
          `/knowledge-bases/${kbId}/rag/chunks/details`,
          chunkIds
        );

        // Build page boundaries lookup
        const pageBoundaries = ocrResponse.data.pageBoundaries || [];
        const convertedChunks = detailsResponse.data.chunks.map((chunk) => {
          const pageNum = chunk.pageNumber || 1;
          // Find the start offset for this page
          const pageStartOffset = pageBoundaries[pageNum - 1] || 0;
          
          // Get the original chunk data to merge scores
          const originalChunk = chunks.find(c => c.chunk_id === chunk.chunkId);
          
          // Look up cached position
          const cachedPos = cachedPositions.get(chunk.chunkId);
          
          const converted = {
            chunkId: chunk.chunkId,
            text: chunk.text || originalChunk?.text || '',
            relevanceScore: originalChunk?.chunk_score || 0,
            pageNumber: pageNum,
            // Convert to per-page offsets
            charStart: Math.max(0, chunk.charStart - pageStartOffset),
            charEnd: Math.max(0, chunk.charEnd - pageStartOffset),
            // Attach pre-computed position if available
            position: cachedPos || undefined,
          };
          
          return converted;
        });

        setEnrichedChunks(convertedChunks);
      }

      setLoading(false);
    } catch (err) {
      console.error('Failed to load document data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load document');
      setLoading(false);
    }
  }, [documentId, kbId, chunks, reloadCounter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRecalculateHighlights = useCallback(async () => {
    if (!kbId || !documentId) return;
    setRecalcLoading(true);
    setError(null);
    try {
      // Force backend bbox path on next load
      setForceBackendBboxes(true);
      await ragService.deleteChunkPositions(kbId, documentId);
      // Bumping the counter re-mints a scoped URL with a cache-buster (effect above)
      setReloadCounter(c => c + 1);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to recalculate highlights');
    } finally {
      setRecalcLoading(false);
    }
  }, [kbId, documentId, loadData]);

  // Callback to save computed positions to backend
  const handlePositionsComputed = useCallback(async (positions: Array<{ chunkId: string; position: ChunkPositionData }>) => {
    if (positions.length === 0) return;
    
    try {
      console.log('[PDFDocumentViewerTab] Saving', positions.length, 'computed positions to backend');
      const result = await ragService.saveChunkPositions(kbId, {
        document_id: documentId,
        positions: positions.map(p => ({
          chunk_id: p.chunkId,
          position: p.position,
        })),
      });
      console.log('[PDFDocumentViewerTab] Saved positions:', result);
      // Reset forced backend after a successful recompute
      setForceBackendBboxes(false);
    } catch (err) {
      console.warn('[PDFDocumentViewerTab] Failed to save positions:', err);
      // Non-critical - positions can be recomputed next time
    }
  }, [kbId, documentId]);

  // Callback to convert scanned PDF to searchable format
  const handleConvertToSearchable = useCallback(async () => {
    console.log('[PDFDocumentViewerTab] ========== CONVERSION TRIGGERED ==========');
    console.log('[PDFDocumentViewerTab] KB ID:', kbId);
    console.log('[PDFDocumentViewerTab] Document ID:', documentId);
    console.log('[PDFDocumentViewerTab] Is converting:', isConverting);
    console.log('[PDFDocumentViewerTab] Enriched chunks count:', enrichedChunks.length);
    
    if (isConverting) {
      console.log('[PDFDocumentViewerTab] Conversion already in progress, skipping');
      return;
    }
    
    if (enrichedChunks.length === 0) {
      console.warn('[PDFDocumentViewerTab] No chunks to convert, skipping');
      return;
    }
    
    setIsConverting(true);
    
    try {
      // Get unique pages that contain chunks
      const pagesWithChunks = [...new Set(enrichedChunks.map(c => c.pageNumber))].sort((a, b) => a - b);
      console.log('[PDFDocumentViewerTab] Pages to convert:', pagesWithChunks.join(','), `(${pagesWithChunks.length} pages)`);
      
      // Call conversion API
      console.log('[PDFDocumentViewerTab] Calling API...');
      const response = await apiClient.post(
        `/knowledge-bases/${kbId}/documents/${documentId}/convert-to-searchable`,
        null,
        { 
          params: { pages_to_convert: pagesWithChunks.join(',') },
          responseType: 'blob'  // Important: receive PDF as binary
        }
      );
      
      console.log('[PDFDocumentViewerTab] API response received');
      console.log('[PDFDocumentViewerTab] Response status:', response.status);
      console.log('[PDFDocumentViewerTab] Response data type:', typeof response.data);
      console.log('[PDFDocumentViewerTab] Response data size:', response.data?.size || 0, 'bytes');
      
      // Create new URL from converted PDF blob
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const newPdfUrl = URL.createObjectURL(blob);
      
      console.log('[PDFDocumentViewerTab] Created blob URL:', newPdfUrl.substring(0, 80));
      console.log('[PDFDocumentViewerTab] Current PDF URL:', pdfUrl.substring(0, 80));
      
      // Update the PDF URL to use converted version
      // This will trigger DocumentViewer to reload with searchable PDF
      setPdfUrl(newPdfUrl);
      console.log('[PDFDocumentViewerTab] ========== CONVERSION COMPLETE ==========');
      
    } catch (err: unknown) {
      const e = err as { name?: string; message?: string; response?: { status?: number; data?: unknown } };
      console.error('[PDFDocumentViewerTab] ========== CONVERSION FAILED ==========');
      console.error('[PDFDocumentViewerTab] Error type:', e?.name);
      console.error('[PDFDocumentViewerTab] Error message:', e?.message);
      console.error('[PDFDocumentViewerTab] Error response:', e?.response?.status, e?.response?.data);
      console.error('[PDFDocumentViewerTab] Full error:', err);
      throw err; // Re-throw so DocumentViewer can handle the error state
    } finally {
      setIsConverting(false);
    }
  }, [kbId, documentId, enrichedChunks, isConverting, pdfUrl]);

  if (loading) {
    return (
      <div className="pdf-dv-loading">
        <div className="pdf-dv-spinner" />
        <p>{t('documentViewer.loading', 'Loading document...')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdf-dv-error">
        <p>{t('documentViewer.error', 'Failed to load document')}</p>
        <p className="pdf-dv-error-detail">{error}</p>
        <button onClick={loadData} className="btn btn-primary">
          {t('common.retry', 'Retry')}
        </button>
      </div>
    );
  }

  // Build ocrData in the format expected by DocumentViewer
  const dvOcrData: import('../libs/document-viewer').OCRData | null = ocrData ? {
    documentId: ocrData.documentId,
    processedDate: ocrData.processedDate,
    pages: ocrData.pages.map(p => ({
      pageNumber: p.pageNumber,
      ocrText: p.ocrText,
      textFormat: p.textFormat,
    })),
    pageBoundaries: ocrData.pageBoundaries || [],
    totalPages: ocrData.totalPages || ocrData.pages.length,
  } : null;
  
  console.log('[PDFDocumentViewerTab] dvOcrData being passed:', dvOcrData);
  console.log('[PDFDocumentViewerTab] dvOcrData is truthy?', !!dvOcrData);
  console.log('[PDFDocumentViewerTab] dvOcrData pages length:', dvOcrData?.pages?.length);

  // Debug: Log chunk data
  console.log('[PDFDocumentViewerTab] === Final Data Summary ===');
  console.log('[PDFDocumentViewerTab] Input chunks:', chunks.length);
  console.log('[PDFDocumentViewerTab] Enriched chunks:', enrichedChunks.length);
  console.log('[PDFDocumentViewerTab] Enriched chunks detail:', JSON.stringify(enrichedChunks, null, 2));
  console.log('[PDFDocumentViewerTab] OCR pages count:', ocrData?.pages?.length);
  console.log('[PDFDocumentViewerTab] Page boundaries:', ocrData?.pageBoundaries);
  console.log('[PDFDocumentViewerTab] OCR data:', JSON.stringify(dvOcrData, null, 2).substring(0, 500) + '...');

  // Get unique page numbers from chunks
  const chunkPages = [...new Set(enrichedChunks.map(c => c.pageNumber))].sort((a, b) => a - b);

  return (
    <div className="pdf-dv-container">
      {/* Chunk summary panel */}
      <div className="pdf-dv-chunk-summary">
        <strong>{enrichedChunks.length} chunk(s)</strong>
        {enrichedChunks.length > 0 && (
          <span className="pdf-dv-chunk-pages">
            {' '}on page(s): {chunkPages.join(', ')}
          </span>
        )}
        {enrichedChunks.length > 0 && (
          <span className="pdf-dv-warning" style={{ color: '#856404', backgroundColor: '#fff3cd', padding: '2px 8px', borderRadius: '3px', marginLeft: '8px', fontSize: '0.75rem' }}>
            ⚠️ If highlights don't appear, check browser console for errors from DocumentViewer
          </span>
        )}
        <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleRecalculateHighlights}
            disabled={recalcLoading}
          >
            {recalcLoading ? t('documentViewer.recalculating', 'Recalculating...') : t('documentViewer.recalculate', 'Recalculate highlights')}
          </button>
        </div>
        <details className="pdf-dv-chunk-details">
          <summary>{t('documentViewer.chunkDetails', 'Details')}</summary>
          <table className="pdf-dv-chunk-table">
            <thead>
              <tr><th>#</th><th>Page</th><th>Start</th><th>End</th><th>Score</th></tr>
            </thead>
            <tbody>
              {enrichedChunks.map((c, i) => (
                <tr key={c.chunkId || i}>
                  <td>{i + 1}</td>
                  <td>{c.pageNumber}</td>
                  <td>{c.charStart}</td>
                  <td>{c.charEnd}</td>
                  <td>{(c.relevanceScore * 100).toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      </div>
      
      {/* Lazy-loaded DocumentViewer with fallback */}
      <Suspense fallback={
        <div className="pdf-dv-loading">
          <div className="pdf-dv-spinner" />
          <p>{t('documentViewer.loadingViewer', 'Loading document viewer...')}</p>
        </div>
      }>
        <DocumentViewer
          documentUrl={pdfUrl}
          documentTitle={documentTitle}
          chunks={enrichedChunks}
          ocrData={dvOcrData}
          language={i18n.language as 'en' | 'fr'}
          onClose={onClose}
          onChunkClick={(chunkId) => {
            console.log('[DV] Chunk clicked:', chunkId);
          }}
          onPositionsComputed={handlePositionsComputed}
          onConvertToSearchable={handleConvertToSearchable}
          showThumbnails={true}
          kbId={kbId}
          documentId={documentId}
          forceBackendBboxes={forceBackendBboxes}
        />
      </Suspense>
    </div>
  );
};

export default PDFDocumentViewerTab;
